import React, { useState, useEffect, useCallback, useMemo } from 'react';
import EventFormModal from './EventFormModal'; // Add this at the top
import moment from 'moment';
import PropTypes from 'prop-types';
import ConfirmModal from './components/ConfirmModal';
import ErrorModal from './components/ErrorModal';
import MobileNavigation from './components/MobileNavigation';
import MobileFooter from './components/MobileFooter';
import SeriesModal from './components/SeriesModal';
import AdminPanel from './components/AdminPanel';
import CustomDateRangeModal from './components/CustomDateRangeModal';
import ListView from './components/ListView';
import ThreeDayView from './components/ThreeDayView';
import {
  Calendar,
  DateLocalizer,
  momentLocalizer,
} from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { useRef, useLayoutEffect } from 'react';
import './App.css';
import { API } from './api/api';
import { formatHours, transformTimelogsToEvents, handlePeopleTimelogToEvent, SYNC_PROJECT_IDS, needsReassign, TYPE_COLORS, DEFAULT_TYPE_COLOR } from './utils/events';
import { createCalendarComponents } from './components/calendar/createCalendarComponents';
import { useContextMenus } from './hooks/useContextMenus';
import { useCalendarMetrics } from './hooks/useCalendarMetrics';
import { useAuthBootstrap } from './hooks/useAuthBootstrap';
import { weekStartOf, mergeWeekIntoEvents, weekResponseToEvents } from './utils/liveTimelogs';
import { localDateKey, wallClock } from './utils/wallClock';
import AuthenticatingModal from './components/AuthenticatingModal';


const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

moment.locale('en', {
  week: {
    dow: 1, // Start week on Monday
  },
  longDateFormat: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
  }
});

const FORMATS = {
    timeGutterFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),

    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,

    selectRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} – ${localizer.format(end, 'HH:mm', culture)}`,

    agendaTimeFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),
  };

// Redirect dashboard route before the component mounts (avoids conditional-hooks lint error)
if (window.location.pathname === '/portal/zrm/dashboard') {
  const apiOrigin = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api$/, '');
  window.location.replace(apiOrigin + '/portal/zrm/dashboard');
}

function App() {
  const [events, setEvents] = useState([]);
  const eventsRef = useRef(events); // latest events for callbacks that must not re-create on every change
  eventsRef.current = events;
  const [projects, setProjects] = useState([]);
  const [currentView, setCurrentView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [error, setError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [slotInfo, setSlotInfo] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventPendingDelete, setEventPendingDelete] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '' });
  const [showPeople, setShowPeople] = useState(false);
  const [showProjects, setShowProjects] = useState(true);
  const [defaultUseNotes, setDefaultUseNotes] = useState(() => {
    return localStorage.getItem('zrm_default_use_notes') === 'true';
  });

  const [showGamification, setShowGamificationState] = useState(() => {
    try { return localStorage.getItem('zrm_show_gamification') !== 'false'; } catch { return true; }
  });
  const setShowGamification = useCallback((val) => {
    setShowGamificationState(val);
    try { localStorage.setItem('zrm_show_gamification', val); } catch {}
  }, []);
  // Sættes ved brugerens egne handlinger, så bundbaren kun fejrer dem (ikke load eller baggrundsopdatering).
  const lastUserMutationAt = useRef(0);
  const markUserMutation = useCallback(() => { lastUserMutationAt.current = Date.now(); }, []);

  const filteredEvents = useMemo(() => {
    return events.filter(ev =>
      (showPeople && ev.isPeople) ||
      (showProjects && !ev.isPeople)
    );
  }, [events, showPeople, showProjects]);

  const stripPrefix = useCallback((id) => {
    if (typeof id !== 'string') return id;
    return id.replace(/^(people|projects):/, '');
  }, []);

  const getModalPositionStyle = useCallback((date, view) => {
    const day = moment(date).day();
    let positionStyle = { right: '20px' };
    if (view === 'week') {
      if (day === 0 || day >= 4) positionStyle = { left: '20px' };
    } else if (view === 'work_week') {
      if (day >= 3) positionStyle = { left: '20px' };
    } else if (view === 'three_day') {
      const eventDate = moment(date).startOf('day');
      let rangeStart = currentDate ? moment(currentDate).startOf('day') : eventDate;
      const dayIndex = eventDate.diff(rangeStart, 'days');
      if (dayIndex === 2 || dayIndex < 0 || dayIndex > 2) {
        positionStyle = { left: '20px' };
      }
    }
    return {
      ...positionStyle,
      position: 'fixed',
      top: '80px',
      zIndex: 2600,
    };
  }, [currentDate]);

  const [modalPosition, setModalPosition] = useState(() => getModalPositionStyle(new Date(), currentView));
  const [isDragging, setIsDragging] = useState(false);

  // Show error in modal instead of taking over the page
  const showError = useCallback((title, message) => {
    setErrorModalData({ title, message });
    setShowErrorModal(true);
  }, []);

  const {
    eventToCopy,
    setEventToCopy,
    contextMenu,
    setContextMenu,
    contextMenuRef,
    slotContextMenu,
    setSlotContextMenu,
    slotContextMenuRef,
    handleRightClickEvent,
    handleRightClickSlot,
  } = useContextMenus();
  const [originalEventBeforeEdit, setOriginalEventBeforeEdit] = useState(null);
  const suppressRevertRef = useRef(false); // <-- add this

  const [selectedSlotDate, setSelectedSlotDate] = useState(null);
  
  // Series Modal state
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [seriesTemplateEvent, setSeriesTemplateEvent] = useState(null);
  
  // Series Delete state
  const [showSeriesDeleteConfirm, setShowSeriesDeleteConfirm] = useState(false);
  const [seriesPendingDelete, setSeriesPendingDelete] = useState({ seriesId: null, events: [] });

  // Custom Date Range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [customRangeData, setCustomRangeData] = useState(null);
  const [isLoadingCustomRange, setIsLoadingCustomRange] = useState(false);

  const [customRangeContext, setCustomRangeContext] = useState('projects'); // NEW

  const [billableAutoFill, setBillableAutoFill] = useState(false);
  const [, setWeeklyHours] = useState(null);
  const [isTimelogsLoaded, setIsTimelogsLoaded] = useState(false);
  const [canUseBD, setCanUseBD] = useState(false); // "Business Development" type - Projektledere only
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Handle Create Series functionality (from right-click)
  const handleCreateSeries = useCallback((templateEvent) => {
    setSeriesTemplateEvent(templateEvent);
    setShowSeriesModal(true);
    setContextMenu({ visible: false, x: 0, y: 0, event: null });
  }, []);

  // Handle Create Series from Header (without template)
  const handleCreateSeriesFromHeader = useCallback(() => {
    // Create a default template event for new series
    const defaultTemplate = {
      title: '',
      projectId: '',
      timecodeId: '',
      billingtype: 'Billable',
      projectName: '',
      timecodeName: '',
      approval_status: 'Pending',
      start: new Date(),
      end: new Date(),
    };
    setSeriesTemplateEvent(defaultTemplate);
    setShowSeriesModal(true);
  }, []);

  // Handle Custom Date Range
  const handleCustomDateRangeClick = useCallback((context = 'projects') => {
    setCustomRangeContext(context);                     // NEW
    setShowCustomDateModal(true);
  }, []);

  

  const handleCustomDateRangeClose = useCallback(() => {
    setShowCustomDateModal(false);
  }, []);

  const handleClearCustomRange = useCallback(() => {
    setCustomDateRange(null);
    setCustomRangeData(null);
  }, []);



  const handleAddPeopleEvent = useCallback((timelogDetails) => {
    markUserMutation();
    const date = new Date(timelogDetails.workDate);

    let start, end;
    if (timelogDetails.fromTimeInTimeFormat && timelogDetails.toTimeInTimeFormat) {
      start = new Date(date);
      const [fromHour, fromMin] = timelogDetails.fromTimeInTimeFormat.split(':').map(Number);
      start.setHours(fromHour, fromMin, 0, 0);

      end = new Date(date);
      const [toHour, toMin] = timelogDetails.toTimeInTimeFormat.split(':').map(Number);
      end.setHours(toHour, toMin, 0, 0);
    } else {
      start = new Date(date);
      start.setHours(8, 0, 0, 0);
      end = new Date(start.getTime() + (timelogDetails.hoursInMins ? timelogDetails.hoursInMins : 60) * 60000);
    }

    const newEvent = {
      title: timelogDetails.description || timelogDetails.jobName || '',
      start,
      end,
      projectId: '',
      timecodeId: timelogDetails.jobId || '',
      projectName: '',
      timecodeName: timelogDetails.jobName || '',
      approval_status: timelogDetails.approvalStatus || '',
      billingtype: timelogDetails.billingStatus === 'billable' ? 'Billable' : 'Non Billable',
      logId: `people:${timelogDetails.timelogId}`,
      isPeople: true,
    };

    setEvents(prev => [...prev, newEvent]);
  });
  
  // Helper function for long press on mobile
  const setupLongPress = useCallback((handler, contextData = null) => ({
    onTouchStart: (e) => {
      const touchTimer = setTimeout(() => {
        const syntheticEvent = {
          preventDefault: () => e.preventDefault(),
          clientX: e.touches[0].clientX,
          clientY: e.touches[0].clientY,
          target: e.target,
          currentTarget: e.currentTarget
        };
        handler(contextData || e, syntheticEvent);
      }, 500); // 500ms long press
      
      e.currentTarget._touchTimer = touchTimer;
    },
    onTouchEnd: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    },
    onTouchMove: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    }
  }), []);

  // Handle Delete Entire Series functionality
  const handleDeleteSeries = useCallback((seriesId) => {
    const seriesEvents = events.filter(event => event.seriesId === seriesId);
    if (seriesEvents.length > 0) {
      setSeriesPendingDelete({ seriesId, events: seriesEvents });
      setShowSeriesDeleteConfirm(true);
      setContextMenu({ visible: false, x: 0, y: 0, event: null });
    }
  }, [markUserMutation, events]);

  // Confirm and execute series deletion
  const handleConfirmDeleteSeries = useCallback(async () => {
    markUserMutation();
    setShowSeriesDeleteConfirm(false);
    try {
      const logsToDelete = seriesPendingDelete.events
        .filter(e => e.logId)
        .map(e => ({ logId: stripPrefix(e.logId), module: e.module }));

      if (logsToDelete.length) {
        await API.deleteTimelogsBulk({ logs: logsToDelete });
      }
      setEvents(prev => prev.filter(event => event.seriesId !== seriesPendingDelete.seriesId));
      setSeriesPendingDelete({ seriesId: null, events: [] });
    } catch (error) {
      console.error('Error deleting series:', error);
      showError('Delete Failed', 'Failed to delete some events in the series. Please try again.');
    }
  }, [markUserMutation, seriesPendingDelete, showError]);

  // Handle batch creation of series events
  const handleBatchCreateSeries = useCallback(async (eventsToCreate, setProgress) => {
    markUserMutation();
    const seriesId = eventsToCreate[0]?.seriesId;
    if (!seriesId) throw new Error('Series ID is required');

    try {
      setProgress({ current: 0, total: eventsToCreate.length });

      const data = await API.createTimelogsBulk({
        events: eventsToCreate.map(e => ({
          projectId: e.projectId,
          timecodeId: e.timecodeId,
          notes: e.title,
          bill_status: e.billingtype,
          date: localDateKey(e.start),
          start: wallClock(e.start),
          end: wallClock(e.end),
          type: e.type,
          akut: !!e.akut,
        })),
      });

      const createdLogs = (data?.data?.time_logs || [])
        .map(day => day.log_details?.[0])
        .filter(Boolean);
      createdLogs.forEach((log, i) => {
        const eventData = eventsToCreate[i];
        const logId = log.id || log.id_string;
        setEvents(prev => [...prev, {
          title: eventData.title,
          start: eventData.start,
          end: eventData.end,
          projectId: eventData.projectId,
          timecodeId: eventData.timecodeId,
          projectName: eventData.projectName || '',
          timecodeName: eventData.timecodeName || '',
          billingtype: eventData.billingtype || '',
          approval_status: 'Pending',
          logId: `projects:${logId}`,
          seriesId: eventData.seriesId,
          type: eventData.type,
        }]);
      });

      setProgress({ current: eventsToCreate.length, total: eventsToCreate.length });
      if (authenticated) {
        API.fetchProjects().then((projectData) => {
          if (projectData.success) setProjects(projectData.data);
        }).catch(() => {});
      }
    } catch (error) {
      console.error('🚨 Error creating series:', error);
      throw error;
    }
  }, [markUserMutation, authenticated, stripPrefix]);

  const createMarkedPlaceholder = useCallback((start, end, allDay) => {
    const tempId = `temp-${Date.now()}`;
    const base = {
      title: '',
      start,
      end,
      logId: tempId,
      approval_status: 'Marked',
      projectId: '',
      timecodeId: '',
      billingtype: '',
      projectName: '',
      timecodeName: '',
    };
    return allDay === undefined ? base : { ...base, allDay };
  }, []);

  // Handle Add New functionality
  const handleAddNew = useCallback(() => {
    // 1. Round up to next 30-minute interval (hel/halv timer)
    const now = (currentView === 'day' || currentView === 'three_day') ? new Date(currentDate) : new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 30) * 30;
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0, 0, 0);
    } else {
      now.setMinutes(roundedMinutes, 0, 0);
    }
    const start = new Date(now);
    const end = new Date(start.getTime() + 60 * 60 * 1000); // +1 hour

    const placeholder = createMarkedPlaceholder(start, end);
    setEvents(prev => [
      ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
      placeholder
    ]);
    setSlotInfo({ start, end, tempId: placeholder.logId });
    setModalPosition(getModalPositionStyle(start, currentView));
    setShowModal(true);
  }, [currentView, currentDate, setEvents, setSlotInfo, setModalPosition, setShowModal, getModalPositionStyle, createMarkedPlaceholder]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger if not typing in an input or textarea and modal is not open
      if (
        e.key === 'n' &&
        !showModal &&
        document.activeElement.tagName !== 'INPUT' &&
        document.activeElement.tagName !== 'TEXTAREA'
      ) {
        handleAddNew();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, handleAddNew]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setContextMenu({ visible: false, x: 0, y: 0, event: null });
      }
      
      if (
        slotContextMenuRef.current &&
        !slotContextMenuRef.current.contains(event.target)
      ) {
        setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
      }
    };

    if (contextMenu.visible || slotContextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.visible, slotContextMenu.visible]);

  const isDraggable = useCallback((event) => event.approval_status !== 'Approved', []);
  const isResizable = useCallback((event) => event.approval_status !== 'Approved', []);

  const calendarRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const previousViewRef = useRef(currentView);
  const previousDateRef = useRef(currentDate);
  const previousAuthRef = useRef(authenticated);

  // Reusable function to scroll calendar to specific time
  const scrollToTime = useCallback((targetTime, smooth = false) => {
    if (!calendarRef.current) return;

    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');
    if (!scrollContainer) return;

    // Extract hour from target time
    const targetHour = typeof targetTime === 'number' ? targetTime : targetTime.getHours();
    const targetMinutes = typeof targetTime === 'number' ? 0 : targetTime.getMinutes();
    
    // Calculate total minutes from start of day
    const totalMinutes = targetHour * 60 + targetMinutes;
    
    // Try to calculate actual pixels per hour from DOM
    const timeSlots = scrollContainer.querySelectorAll('.rbc-time-slot');
    let pixelsPerHour = 40; // fallback
    
    if (timeSlots.length > 0) {
      const slotHeight = timeSlots[0].offsetHeight;
      const slotsPerHour = 2; // 30-minute intervals = 2 slots per hour
      pixelsPerHour = slotHeight * slotsPerHour;
    }
    
    // Calculate scroll position - add some padding above for context
    const paddingHours = 1; // Show 1 hour above for context
    const scrollPosition = Math.max(0, (totalMinutes / 60 - paddingHours) * pixelsPerHour);
    
    console.log('Scrolling to time:', {
      targetTime: typeof targetTime === 'number' ? `${targetTime}:00` : targetTime.toTimeString(),
      targetHour,
      targetMinutes,
      totalMinutes,
      pixelsPerHour,
      scrollPosition,
      smooth
    });
    
    if (smooth) {
      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    } else {
      scrollContainer.scrollTop = scrollPosition;
    }
  }, []);

  useLayoutEffect(() => {
    if (!calendarRef.current || showModal) return; // <- skip if modal is open

    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');

    if (scrollContainer) {
      // Check if view, date, or auth status has changed
      const viewChanged = previousViewRef.current !== currentView;
      const dateChanged = previousDateRef.current !== currentDate;
      const authChanged = previousAuthRef.current !== authenticated;
      
      // Only auto-scroll on initial load or when view/date/auth changes, not when events are updated
      if (!hasAutoScrolledRef.current || viewChanged || dateChanged || authChanged) {
        // Get current time and scroll to it (with some padding above)
        const now = new Date();
        const currentHour = now.getHours();
        
        // Go back 2 hours for context, but not below 6 AM
        let targetHour = Math.max(6, currentHour - 2);
        
        // If it's early morning (before 8 AM), just start at 6 AM
        if (currentHour < 8) {
          targetHour = 6;
        }
        
        // Debug info
        const reason = !hasAutoScrolledRef.current ? 'initial load' : 
                      (viewChanged ? 'view change' : 
                       (dateChanged ? 'date change' : 'auth change'));
        console.log('Auto-scroll:', { currentHour, targetHour, time: now.toTimeString(), reason });
        
        // Use the reusable scroll function
        scrollToTime(targetHour, false);
        hasAutoScrolledRef.current = true;
      }
      
      // Always update previous values for next comparison (regardless of whether we scrolled)
      previousViewRef.current = currentView;
      previousDateRef.current = currentDate;
      previousAuthRef.current = authenticated;
    }
  }, [currentView, showModal, currentDate, scrollToTime, authenticated]); // Added 'authenticated' to ensure auto-scroll works after login

  useAuthBootstrap({
    authenticated,
    setAuthenticated,
    setProjects,
    setEvents,
    showError,
    setError,
    setBillableAutoFill,
    setCurrentView,
    setWeeklyHours,
    setIsTimelogsLoaded,
    setCanUseBD,
  });

  // The week on screen, fresh from Zoho, merged over the (up to 15 min old) full load.
  // Runs on load, on every week change and when the tab regains focus, so logs created
  // outside the calendar (ClaudeCodeSync, the Zoho UI) show up. No polling: Zoho locks
  // the timelog read URL after about 200 reads. Failures keep what is on screen.
  const visibleWeekStart = weekStartOf(currentDate);
  const refreshVisibleWeek = useCallback(() => {
    API.fetchProjectsTimelogsWeek(visibleWeekStart)
      .then((response) => {
        if (!response?.success) return;
        const fresh = weekResponseToEvents(response.data);
        setEvents(prev => mergeWeekIntoEvents(prev, fresh, visibleWeekStart));
      })
      .catch(() => {});
  }, [visibleWeekStart]);

  useEffect(() => {
    if (!authenticated || !isTimelogsLoaded) return;
    refreshVisibleWeek();
    const onFocus = () => {
      if (document.visibilityState === 'visible') refreshVisibleWeek();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [authenticated, isTimelogsLoaded, refreshVisibleWeek]);

  // Save user's view preference whenever currentView changes
  useEffect(() => {
    if (authenticated && currentView) {
      API.saveUserViewPreference(currentView).catch(() => {
        // Silently fail - not critical
      });
    }
  }, [currentView, authenticated]);


  const fetchProjects = useCallback(() => {
    API.fetchProjects()
      .then((projectData) => {
        if (projectData.success) {
          setProjects(projectData.data);
        } else {
          showError('Projects Error', 'Failed to fetch projects');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Failed to fetch projects. Please check your connection.');
      });
  }, [showError]);

  const eventPropGetter = useCallback((event) => {
    // Dark-mode event palette
    // Today column is blue (rgba 32,87,153) — keep events in different hues
    let backgroundColor = '#1ea582'; // Zoho Projects
    let borderStyle = 'solid';
    let borderColor = '#28C99E';
    const isRejectedOrChanged = event.approval_status === 'Rejected' || event.approval_status === 'Changed';

    if (event.isPeople) {
      backgroundColor = '#ba94b4'; // Zoho People
      borderColor = '#D4B0CE';
    } else if (SYNC_PROJECT_IDS.has(event.projectId)) {
      backgroundColor = '#007479'; // synced calendar events (e.g. Google Calendar)
      borderColor = '#00A8AF';
    } else {
      const typeColor = TYPE_COLORS[event.type] || DEFAULT_TYPE_COLOR;
      const shade = event.approval_status === 'Approved' ? typeColor.normal : typeColor.light;
      backgroundColor = shade.backgroundColor;
      borderColor = shade.borderColor;
      if (isRejectedOrChanged) {
        borderStyle = 'dashed';
      }
    }

    if (event.draft) {
      borderStyle = 'dashed'; // kladde: not a registration until saved from the form
    }

    if (event.approval_status === 'Marked') {
      backgroundColor = 'rgba(255, 120, 0, 0.15)';
      borderStyle = 'dashed';
      borderColor = 'rgba(255, 120, 0, 0.5)';
    } else if (editingEvent?.logId === event.logId) {
      backgroundColor = '#3D3000'; // dark gold tint for currently editing
      borderStyle = 'solid';
      borderColor = '#FF7800';
    }

    return {
      style: {
        backgroundColor,
        color: 'rgba(255, 255, 255, 0.92)',
        borderRadius: '4px',
        border: `1px ${borderStyle} ${borderColor}`,
        padding: '2px 4px',
        opacity: event.approval_status === 'Marked' ? 0.9 : (isRejectedOrChanged || event.draft) ? 0.75 : 1,
      },
    };
  }, [editingEvent]);

  const {
    totalsCurrentView,
    totalsCurrentViewPeople,
    totalsYTD,
    totalsYTDPeople,
    getDailyTotals,
    getCustomPeriodTotals,
    getYearOverYearComparison,
    isOutlookSyncEvent,
    isBillableEvent,
  } = useCalendarMetrics({ events, currentDate, currentView });

  const handleCustomDateRangeApply = useCallback((range) => {
    setIsLoadingCustomRange(true);
    setCustomDateRange({ ...range, context: customRangeContext }); // NEW

    try {
      if (range.compareWithPreviousYear) {
        const comparisonData = getYearOverYearComparison(range.start, range.end, customRangeContext); // NEW
        setCustomRangeData({
          ...comparisonData,
          range: { ...range, context: customRangeContext }, // NEW
          label: range.label || 'Custom Range',
        });
      } else {
        const currentPeriod = getCustomPeriodTotals(range.start, range.end, customRangeContext); // NEW
        setCustomRangeData({
          current: currentPeriod,
          range: { ...range, context: customRangeContext }, // NEW
          label: range.label || 'Custom Range',
          hasData: currentPeriod.total > 0,
        });
      }
    } catch (error) {
      console.error('Error calculating custom range data:', error);
      showError('Calculation Error', 'An error occurred while calculating the selected period');
    } finally {
      setIsLoadingCustomRange(false);
    }
  }, [customRangeContext, showError, getCustomPeriodTotals, getYearOverYearComparison]);

  const handleEventDrop = useCallback(({ event, start, end, allDay }) => {
    markUserMutation();
    setIsDragging(false);
    setModalPosition(getModalPositionStyle(start, currentView));
    setOriginalEventBeforeEdit(prev => prev ?? event);
    
    // Check if the new duration would exceed 23 hours
    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }
    
    // If it's a marked (temporary) event, remove it and create a new marked event at the new spot
    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
      if (isMarked) {
        setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
        const tempId = `temp-${Date.now()}`;
        const placeholder = {
          title: '',
          start,
          end,
          allDay,
          logId: tempId,
          approval_status: 'Marked',
          projectId: '',
          timecodeId: '',
          billingtype: '',
          projectName: '',
          timecodeName: '',
        };
        setEvents(prev => [...prev, placeholder]);
        setSlotInfo({ start, end, tempId });
        setEditingEvent(null);
        setShowModal(true);
        setOriginalEventBeforeEdit(null);
        return;
      }
      if (event.approval_status !== 'Marked') {
        setEditingEvent(event);
        // Remove marked events if selected is not marked
        setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
      }
      // Normal event: open modal for editing
      const updated = { ...event, start, end, allDay, approval_status: 'Changed' };
      setEditingEvent(updated);
      setShowModal(true);
      setEvents((prev) =>
        prev.map((ev) => (ev === event ? updated : ev))
      );
    }, [markUserMutation, currentView]);

  const handleEventResize = useCallback(({ event, start, end }) => {
    markUserMutation();
    setIsDragging(false);
    setOriginalEventBeforeEdit(prev => prev ?? event);
    
    // Check if the new duration would exceed 23 hours
    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }
    
    // If it's a marked (temporary) event, remove it and create a new marked event at the new spot
    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
    if (isMarked) {
      setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start,
        end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [...prev, placeholder]);
      setSlotInfo({ start, end, tempId });
      setEditingEvent(null);
      setShowModal(true);
      setOriginalEventBeforeEdit(null);
      return;
    }

    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    // Normal event: open modal for editing
    const updated = { ...event, start, end, approval_status: 'Changed' };
    //setOriginalEventBeforeEdit(event);
    setEditingEvent(updated);
    setShowModal(true);
    setEvents((prev) =>
      prev.map((ev) => (ev === event ? updated : ev))
    );
  }, [markUserMutation]);

  const createTimelogFromEvent = useCallback((newEvent) => {
    return API.createTimelog({
      start: wallClock(newEvent.start),
      end: wallClock(newEvent.end),
      projectId: newEvent.projectId,
      timecodeId: newEvent.timecodeId,
      notes: newEvent.title,
      bill_status: newEvent.billingtype,
      date: localDateKey(newEvent.start),
      type: newEvent.type,
      akut: !!newEvent.akut,
      taskDone: !!newEvent.taskDone,
      selectedTaskId: newEvent.selectedTaskId || '',
      createTask: !!newEvent.createTask,
      ytdNonBillableHours: newEvent.ytdNonBillableHours ?? 0,
    }).then((data) => {
      if (data.success) {
        const newEventWithId = {
          title: newEvent.title,
          start: newEvent.start,
          end: newEvent.end,
          projectId: newEvent.projectId,
          timecodeId: newEvent.timecodeId,
          projectName: newEvent.projectName || '',
          timecodeName: newEvent.timecodeName || '',
          billingtype: newEvent.billingtype || '',
          approval_status: 'Pending',
          logId: `projects:${data.data.id}`,
          type: newEvent.type || '',
          akut: !!newEvent.akut,
          selectedTaskId: newEvent.selectedTaskId || ''
        };
        setEvents((prev) => [...prev, newEventWithId]);
        fetchProjects();
      }
      return data;
    });
  }, [fetchProjects]);

  const handleSaveEvent = useCallback((newEvent) => {
    markUserMutation();
    createTimelogFromEvent(newEvent)
      .then((data) => {
        if (!data.success) {
          showError('Save Failed', 'Failed to save event. Please try again.');
        }
      })
      .catch((err) => {
        showError('Save Failed', err.message || 'Server error while saving event. Please check your connection and try again.');
      });
  }, [markUserMutation, createTimelogFromEvent, showError]);

  // Synced (e.g. Google Calendar) timelogs already belong to their sync project in Zoho.
  // Zoho's update endpoint can't move a log to a different project, so reassigning one
  // means deleting the original log and creating a fresh one on the newly selected project.
  const handleReassignSyncEvent = useCallback((oldEvent, newEvent) => {
    markUserMutation();
    const cleanLogId = stripPrefix(oldEvent.logId);
    API.deleteTimelog({ logId: cleanLogId, projectId: oldEvent.projectId, timecodeId: oldEvent.timecodeId, module: oldEvent.module })
      .then((data) => {
        if (!data.success) {
          showError('Update Failed', 'Failed to move the synced event to the new project. Please try again.');
          return;
        }
        setEvents((prev) => prev.filter((ev) => ev.logId !== oldEvent.logId));
        return createTimelogFromEvent(newEvent).then((data2) => {
          if (!data2.success) {
            showError('Save Failed', 'The synced event was removed, but creating it on the new project failed. Please re-add it manually.');
          }
        });
      })
      .catch((err) => {
        showError('Update Failed', err.message || 'Server error while moving the synced event.');
      });
  }, [markUserMutation, createTimelogFromEvent, showError, stripPrefix]);

  const handleUpdateEvent = useCallback((updatedEvent) => {
    markUserMutation();
    API.updateTimelog({
      start: wallClock(updatedEvent.start),
      end: wallClock(updatedEvent.end),
      projectId: updatedEvent.projectId,
      timecodeId: updatedEvent.timecodeId,
      notes: updatedEvent.title,
      bill_status: updatedEvent.billingtype,
      date: localDateKey(updatedEvent.start),
      logId: stripPrefix(updatedEvent.logId),
      module: updatedEvent.module,
      type: updatedEvent.type,
      previousType: updatedEvent.previousType,
      akut: !!updatedEvent.akut,
      taskDone: !!updatedEvent.taskDone,
      selectedTaskId: updatedEvent.selectedTaskId || '',
      ytdNonBillableHours: updatedEvent.ytdNonBillableHours ?? 0,
    })
      .then((data) => {
        if (data.success) {
          setEvents((prev) =>
            prev.map((ev) =>
              ev.logId === updatedEvent.logId ? { ...ev, ...updatedEvent } : ev
            )
          );
          setOriginalEventBeforeEdit(null);
        } else {
          showError('Update Failed', 'Failed to update event. Please try again.');
        }
      })
      .catch((err) => {
        showError('Update Failed', err.message || 'Server error while updating event. Please check your connection and try again.');
      });
  }, [markUserMutation, showError]);

  const handleDeleteEvent = useCallback((logId, projectId, timecodeId, isPeople) => {
    markUserMutation();
    if (isPeople) {
      API.deletePeopleTimelog({ timeLogId: stripPrefix(logId) })
        .then((data) => {
          if (data.success) {
            setEvents((prev) => prev.filter((ev) => ev.logId !== logId));
          } else {
            showError('Delete Failed', 'Failed to delete Zoho People timelog. Please try again.');
          }
        })
        .catch(() => {
          showError('Connection Error', 'Server error while deleting Zoho People timelog.');
        });
      return;
    }

    // ✅ NEW: Check if it's a CRM log BEFORE stripping prefix
    const isCRMLog = logId.toString().includes('crm:');
    
    // ✅ For CRM logs: keep the full logId with 'crm:' prefix
    // ✅ For Projects logs: strip the 'projects:' prefix
    const cleanLogId = isCRMLog ? logId : stripPrefix(logId);

    const module = eventsRef.current.find((ev) => ev.logId === logId)?.module;
    API.deleteTimelog({ logId: cleanLogId, projectId, timecodeId, module })
      .then((data) => {
        if (data.success) {
          setEvents((prev) => prev.filter((ev) => ev.logId !== logId));
        } else {
          showError('Delete Failed', 'Failed to delete event. Please try again.');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Server error while deleting event. Please check your connection and try again.');
      });
  }, [markUserMutation, showError, stripPrefix]);

  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const handleUpdatePeopleEvent = useCallback((updatedTimelogDetails) => {
    markUserMutation();
    console.log('Zoho People updated event:', updatedTimelogDetails);
    const updatedEvent = handlePeopleTimelogToEvent(updatedTimelogDetails);
    if (!updatedEvent) return;

    console.log('Updated event:', updatedEvent);

    setEvents(prev =>
      prev.map(ev => (ev.logId === updatedEvent.logId ? { ...ev, ...updatedEvent } : ev))
    );

    suppressRevertRef.current = true; // <-- prevent revert on modal close
    setOriginalEventBeforeEdit(null);
  }, [markUserMutation]);

  const max = useMemo(() => new Date(2025, 4, 10, 23, 59), []);
  const timeslots = 2;
  const views = {
    month: true,
    week: true,
    work_week: true,
    three_day: ThreeDayView,
    day: true,
    list: true
  };

  // Handler for day click in month view - navigates to day view
  const handleDayClick = useCallback((date) => {
    setCurrentDate(date);
    setCurrentView('day');
  }, []);

  const handleSelectSlot = useCallback((slotInfo, e) => {
    // In month view, clicking on a date should navigate to day view
    if (currentView === 'month') {
      handleDayClick(slotInfo.start);
      return;
    }

    // Check if this is a right-click
    if (e && e.type === 'contextmenu') {
      handleRightClickSlot(slotInfo, e);
      return;
    }

    // If modal is already open, don't create a new event
    if (showModal) {
      return;
    }

    // Restore previous event if editing and changes were not saved
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setEditingEvent(null);
    setIsDragging(false);
    setSelectedSlotDate(slotInfo.start);
    setModalPosition(getModalPositionStyle(slotInfo.start, currentView));

    if (eventToCopy) {
      const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
      const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
      const copiedEvent = {
        ...eventToCopy,
        start: slotInfo.start,
        end: newEnd,
      };
      handleSaveEvent(copiedEvent);
      setEventToCopy(null);
    } else {
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start: slotInfo.start,
        end: slotInfo.end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [
        ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
        placeholder
      ]);
      setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId });
      setShowModal(true);
    }
  }, [eventToCopy, handleSaveEvent, currentView, originalEventBeforeEdit, handleDayClick, handleRightClickSlot, showModal]);

  const handleSelectEvent = useCallback((event) => {
    // Restore previous event if editing and changes were not saved
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setIsDragging(false);
    setShowDeleteConfirm(false); 
    setEventPendingDelete(null); 
    setOriginalEventBeforeEdit(event);

    // Only set editing event if it's not a marked event
    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    setShowModal(true);
  }, [originalEventBeforeEdit]);

  const handleModalTimeChange = useCallback((updatedEvent) => {
    setEvents((prevEvents) =>
      prevEvents.map(ev =>
        // For editing: match by logId, for marked: match by tempId or start/end
        (updatedEvent.logId && ev.logId === updatedEvent.logId) ||
        (!updatedEvent.logId && slotInfo?.tempId && ev.logId === slotInfo.tempId)
          ? { ...ev, start: updatedEvent.start, end: updatedEvent.end }
          : ev
      )
    );
    // Also update editingEvent if present
    setEditingEvent((prev) =>
      prev && prev.logId === updatedEvent.logId
        ? { ...prev, start: updatedEvent.start, end: updatedEvent.end }
        : prev
    );
    // Also update slotInfo for marked events
    if (!updatedEvent.logId && slotInfo) {
      setSlotInfo((prev) => prev ? { ...prev, start: updatedEvent.start, end: updatedEvent.end } : prev);
    }
    
    // 🚀 Auto-scroll calendar to follow the event when moved with arrow keys
    if (updatedEvent.start && (currentView === 'day' || currentView === 'three_day' || currentView === 'week' || currentView === 'work_week')) {
      // Use smooth scrolling for better UX
      scrollToTime(updatedEvent.start, true);
    }
  }, [slotInfo, currentView, scrollToTime]);

  const eventElementMapRef = useRef(new WeakMap());

  // Memoize calendar sub-components (headers, event renderers, month cell)
  const calendarComponents = useMemo(() => createCalendarComponents({
    currentView,
    formatHours,
    getDailyTotals,
    handleDayClick,
    handleRightClickEvent,
    setupLongPress,
    eventElementMapRef,
  }), [currentView, getDailyTotals, handleDayClick, handleRightClickEvent, setupLongPress]);
  
  if (error && (error.includes('Error 1') || error.includes('Error 2') || error.includes('Error 3'))) {
    let title, message, details;
    
    if (error.includes('Error 1')) {
      title = 'Authentication Required';
      message = 'Unable to verify your access credentials.';
      details = 'The authentication parameter is missing from your URL.';
    } else if (error.includes('Error 2')) {
      title = 'Login Failed';
      message = 'Authentication could not be completed.';
      details = 'Your login credentials could not be verified with the server.';
    } else if (error.includes('Error 3')) {
      title = 'Connection Error';
      message = 'Unable to connect to the authentication server.';
      details = 'Please check your internet connection and try again.';
    }

    return (
      <div className="min-h-screen bg-[#0D1A27] flex items-center justify-center p-4">
        <div className="bg-[#111e2e] border border-[#273C61] p-8 rounded-lg w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.924-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{title}</h1>
            <p className="text-white/70 mb-4">{message}</p>
            <p className="text-sm text-white/50 mb-6">{details}</p>
            <div className="bg-[#0D1A27] rounded-lg p-4">
              <p className="text-sm text-white">
                If the issue persists, please contact the IT department.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <AuthenticatingModal isOpen={true} />;
  }

  


  return (
    <div ref={calendarRef} className="h-screen flex flex-col bg-[#0D1A27] app-container">
      {/* Responsive Navigation */}
      <MobileNavigation
        currentView={currentView}
        setCurrentView={setCurrentView}
        currentDate={currentDate}
        handleNavigate={handleNavigate}
        onAddNew={handleAddNew}
        onCreateSeries={handleCreateSeriesFromHeader}
        showPeople={showPeople}
        setShowPeople={setShowPeople}
        showProjects={showProjects}
        setShowProjects={setShowProjects}
        defaultUseNotes={defaultUseNotes}
        setDefaultUseNotes={(val) => {
          setDefaultUseNotes(val);
          localStorage.setItem('zrm_default_use_notes', val);
        }}
        canUseBD={canUseBD}
        onOpenAdminPanel={() => setShowAdminPanel(true)}
        showGamification={showGamification}
        setShowGamification={setShowGamification}
      />
      {/* Calendar Container */}
      <div 
        className={`flex-1 calendar-container-mobile md:calendar-container-tablet lg:calendar-container-desktop${showGamification ? ' with-armor-footer' : ''}`}
        onContextMenu={(e) => {
          // Check if the right-click was on a calendar slot (not on an event)
          const target = e.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');

          if (isEvent) {
            const calEvent = eventElementMapRef.current.get(isEvent);
            if (calEvent) {
              handleRightClickEvent(calEvent, e);
              return;
            }
          }

          if (!isEvent && isSlot && currentView !== 'month') {
            // Calculate the actual slot time from mouse position
            // Use a much simpler approach - just calculate from pixel position
            const calendarRect = e.currentTarget.getBoundingClientRect();
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = e.clientY - columnRect.top;
              
              // Get the time gutter to understand the time scale
              const timeView = e.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;
              
              let startHour = 0; // Default start hour (midnight)
              let endHour = 24; // Default end hour  
              let totalHours = 24; // Default hours shown (full day)
              
              if (timeGutter) {
                // Try to get actual start/end from time labels
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  // Get first time
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) {
                      startHour = parseInt(match[1]);
                    }
                  }
                  
                  // Get last time  
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) {
                      endHour = parseInt(match[1]) + 1; // Add 1 to get end hour
                    }
                  }
                  
                  totalHours = endHour - startHour;
                  console.log(`Calendar time range: ${startHour}:00 - ${endHour}:00 (${totalHours} hours)`);
                }
              }
              
              // Simple pixel-based calculation
              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;
              
              console.log(`Simple calculation: mouseY=${mouseY}, height=${columnRect.height}, percent=${percentDown.toFixed(3)}, hours=${hoursFromStart.toFixed(2)} → ${finalHour}:${finalMinute.toString().padStart(2, '0')}`);
            
              // Create the slot start time for today (or current date in view)
              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);
             
              // If we're in day view and it's a different day, use that day
              if (currentView === 'day' || currentView === 'three_day') {
                // Use the date from the day/three_day view
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                // For week views, we need to determine which day column was clicked
                // Find the correct day column selector - react-big-calendar uses different classes
                const timeView = e.currentTarget.querySelector('.rbc-time-view');
                const dayColumns = timeView ? timeView.querySelectorAll('.rbc-time-column') : [];
                let dayIndex = 0;
                
                // Alternative approach: find all day header elements to map positions
                const headers = e.currentTarget.querySelectorAll('.rbc-header');
                
                if (headers.length > 0) {
                  // Use headers to determine day positions
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (e.clientX >= headerRect.left && e.clientX <= headerRect.right) {
                      dayIndex = i;
                      console.log(`Slot clicked: Header ${i} (${headers[i].textContent?.trim()})`);
                      break;
                    }
                  }
                } else if (dayColumns.length > 0) {
                  // Fallback to time columns
                  for (let i = 0; i < dayColumns.length; i++) {
                    const dayRect = dayColumns[i].getBoundingClientRect();
                    if (e.clientX >= dayRect.left && e.clientX <= dayRect.right) {
                      dayIndex = i;
                      console.log(`Slot clicked: Column ${i}`);
                      break;
                    }
                  }
                }
                
                // Calculate the date for this day column
                // Always use isoWeek (Monday start) to match calendar display
                let startOfWeek = moment(currentDate).startOf('isoWeek');
                
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
                
                console.log(`Slot calculated: ${moment(slotStart).format('dddd YYYY-MM-DD HH:mm')}`);
              }
             
              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000); // +1 hour
              
              const slotInfo = {
                start: slotStart,
                end: slotEnd
              };
              
              handleRightClickSlot(slotInfo, e);
            }
          }
        }}
        {...setupLongPress((e, syntheticEvent) => {
          // Reuse the same logic as onContextMenu for slots
          const target = syntheticEvent.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');
          
          if (!isEvent && isSlot && currentView !== 'month') {
            // Calculate slot time and handle long press like right-click
            const calendarRect = syntheticEvent.currentTarget.getBoundingClientRect();
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = syntheticEvent.clientY - columnRect.top;
              
              const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;
              
              let startHour = 0;
              let endHour = 24;
              let totalHours = 24;
              
              if (timeGutter) {
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) {
                      startHour = parseInt(match[1]);
                    }
                  }
                  
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) {
                      endHour = parseInt(match[1]) + 1;
                    }
                  }
                  
                  totalHours = endHour - startHour;
                }
              }
              
              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;
              
              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);
              
              if (currentView === 'day' || currentView === 'three_day') {
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
                const headers = syntheticEvent.currentTarget.querySelectorAll('.rbc-header');
                let dayIndex = 0;
                
                if (headers.length > 0) {
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (syntheticEvent.clientX >= headerRect.left && syntheticEvent.clientX <= headerRect.right) {
                      dayIndex = i;
                      break;
                    }
                  }
                }
                
                let startOfWeek = moment(currentDate).startOf('isoWeek');
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              }
              
              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
              const slotInfo = {
                start: slotStart,
                end: slotEnd
              };
              
              handleRightClickSlot(slotInfo, syntheticEvent);
            }
          }
        })}
      >
{currentView === 'list' ? (
          <ListView
            events={filteredEvents}
            currentDate={currentDate}
            onSelectEvent={handleSelectEvent}
            onRightClickEvent={handleRightClickEvent}
            setupLongPress={setupLongPress}
          />
        ) : (
          <DnDCalendar
            events={filteredEvents}
            onDragStart={() => setIsDragging(true)}
            localizer={localizer}
            date={currentDate}
            onNavigate={handleNavigate}
            startAccessor="start"
            endAccessor="end"
            view={currentView}
            onView={setCurrentView}
            views={views}
            max={max}
            step={30}
            snapToSlot={false}
            timeslots={timeslots}
            showMultiDayTimes
            selectable
            popup
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            resizable
            formats={FORMATS}
            eventPropGetter={eventPropGetter}
            draggableAccessor={isDraggable}
            resizableAccessor={isResizable}
            components={calendarComponents}
          />
        )}
      </div>

      {/* Responsive Stats Footer */}
      <MobileFooter
        totalsCurrentView={totalsCurrentView}
        totalsCurrentViewPeople={totalsCurrentViewPeople}
        totalsYTD={totalsYTD}
        currentView={currentView}
        currentDate={currentDate}
        customRangeData={customRangeData}
        isLoadingCustomRange={isLoadingCustomRange}
        onCustomDateRangeClick={handleCustomDateRangeClick}
        onClearCustomRange={handleClearCustomRange}
        totalsYTDPeople={totalsYTDPeople}
        events={events}
        isOutlookSyncEvent={isOutlookSyncEvent}
        isBillableEvent={isBillableEvent}
        mutationAtRef={lastUserMutationAt}
        showGamification={showGamification}
        projects={projects}
      />

      {/* Context Menu for Events */}
      {contextMenu.visible && (
        <ul
          ref={contextMenuRef}
          className="absolute bg-[#0D1A27] border border-[#273C61] shadow-lg rounded-md p-2 z-50"
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
          }}
        >
          <li
            className="px-2 py-1 cursor-pointer text-white hover:bg-[#273C61] rounded"
            onClick={() => {
              setEventToCopy(contextMenu.event);
              setContextMenu({ visible: false, x: 0, y: 0, event: null });
            }}
          >
            Copy
          </li>
          <li
            className="px-2 py-1 cursor-pointer text-white hover:bg-[#273C61] rounded"
            onClick={() => {
              handleCreateSeries(contextMenu.event);
            }}
          >
            Create Series
          </li>
          {/* Show Delete Entire Series if this event is part of a series */}
          {contextMenu.event?.seriesId && (
            <li
              className="px-2 py-1 cursor-pointer text-[#FF7800] hover:bg-[#273C61] rounded"
              onClick={() => {
                handleDeleteSeries(contextMenu.event.seriesId);
              }}
            >
              Delete Entire Series
            </li>
          )}
          {/* Only show Edit and Delete if not Approved */}
          {contextMenu.event?.approval_status !== 'Approved' && (
            <>
              <li
                className="px-2 py-1 cursor-pointer text-white hover:bg-[#273C61] rounded"
                onClick={() => {
                  setEditingEvent(contextMenu.event);
                  setShowModal(true);
                  setContextMenu({ visible: false, x: 0, y: 0, event: null });
                }}
              >
                Edit
              </li>
              <li
                className="px-2 py-1 cursor-pointer text-red-400 hover:bg-[#273C61] rounded"
                onClick={() => {
                  setEventPendingDelete(contextMenu.event);
                  setShowDeleteConfirm(true);
                  setContextMenu({ visible: false, x: 0, y: 0, event: null });
                }}
              >
                Delete
              </li>
            </>
          )}
        </ul>
      )}

      {/* Context Menu for Empty Slots */}
      {slotContextMenu.visible && (
        <ul
          ref={slotContextMenuRef}
          className="absolute bg-[#0D1A27] border border-[#273C61] shadow-lg rounded-md p-2 z-50"
          style={{
            top: slotContextMenu.y,
            left: slotContextMenu.x,
          }}
        >
          <li
            className="px-2 py-1 cursor-pointer text-white hover:bg-[#273C61] rounded"
            onClick={() => {
              const slotInfo = slotContextMenu.slot;
              const placeholder = createMarkedPlaceholder(slotInfo.start, slotInfo.end);
              setEvents(prev => [
                // fix: startswith -> startsWith
                ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
                placeholder
              ]);
              setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId: placeholder.logId });
              setModalPosition(getModalPositionStyle(slotInfo.start, currentView));
              setShowModal(true);
              setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
            }}
          >
            Add New
          </li>
          {eventToCopy && (
            <li
              className="px-2 py-1 cursor-pointer text-white hover:bg-[#273C61] rounded"
              onClick={() => {
                // Paste copied event at this slot
                const slotInfo = slotContextMenu.slot;
                const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
                const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
                const copiedEvent = {
                  ...eventToCopy,
                  start: slotInfo.start,
                  end: newEnd,
                };
                
                handleSaveEvent(copiedEvent);
                setEventToCopy(null);
                setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
              }}
            >
              Paste
            </li>
          )}
        </ul>
      )}

      {!isDragging && (
        <EventFormModal
        defaultUseNotes={defaultUseNotes}
        onAddPeopleEvent={handleAddPeopleEvent}
        onUpdatePeopleEvent={handleUpdatePeopleEvent}
        onTimeChange={handleModalTimeChange}
        onNavigate={handleNavigate}
        currentDate={currentDate}
        key={editingEvent?.logId || slotInfo?.tempId || 'new'}
        modalPosition={modalPosition}
        setModalPosition={setModalPosition}
        getModalPositionStyle={getModalPositionStyle}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingEvent(null);
          if (slotInfo?.tempId) {
            setEvents(prev => prev.filter(ev => ev.logId !== slotInfo.tempId));
          }
          setSlotInfo(null);
          // Only restore if we didn't just save/update
          if (!suppressRevertRef.current && originalEventBeforeEdit) {
            setEvents(prev =>
              prev.map(ev =>
                ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
              )
            );
          }
          setOriginalEventBeforeEdit(null);
          suppressRevertRef.current = false; // reset flag
        }}
        onSave={async (eventData) => {
          // Only for editing, not new
         if (editingEvent) {
          // Subtract the old log's non-billable duration from YTD so the server
          // compares the net change rather than double-counting the existing log.
          // Projects entries are billable only for type Udvikling/Workshop; People
          // entries (no `type` field) keep the original billingtype-based check.
          const oldWasBillable = editingEvent.isPeople
            ? editingEvent.billingtype === 'Billable'
            : (editingEvent.type === 'Udvikling' || editingEvent.type === 'Workshop');
          const oldNonBillableHours = !oldWasBillable
            ? (editingEvent.end - editingEvent.start) / 3600000
            : 0;
          if (needsReassign(editingEvent, eventData.projectId)) {
            handleReassignSyncEvent(editingEvent, {
              ...eventData,
              ytdNonBillableHours: totalsYTD.nonBillable - oldNonBillableHours,
            });
          } else {
            handleUpdateEvent({
              ...eventData,
              logId: editingEvent.logId,
              // Lets the server allow a no-op save of an existing Business
              // Development entry even for a user who can no longer pick BD
              // themselves — only a genuinely new BD assignment is blocked.
              previousType: editingEvent.type,
              module: editingEvent.module,
              ytdNonBillableHours: totalsYTD.nonBillable - oldNonBillableHours,
            });
          }
        } else {
          handleSaveEvent({ ...eventData, ytdNonBillableHours: totalsYTD.nonBillable });
        }
        setShowModal(false);
        setEditingEvent(null);
        setSlotInfo(null);
        setOriginalEventBeforeEdit(null);
        }}
        onDelete={handleDeleteEvent}
        start={editingEvent?.start || slotInfo?.start}
        end={editingEvent?.end || slotInfo?.end}
        projects={projects}
        initialData={editingEvent}
        selectedSlotDate={selectedSlotDate}
        currentView={currentView}
        billableAutoFill={billableAutoFill}
        canUseBD={canUseBD}
      />
      )}
      <ConfirmModal 
        isOpen={showDeleteConfirm}
        onConfirm={() => {
          if (eventPendingDelete) {
            handleDeleteEvent(eventPendingDelete.logId, eventPendingDelete.projectId, eventPendingDelete.timecodeId, eventPendingDelete.isPeople);
            setEventPendingDelete(null);
            setShowDeleteConfirm(false);
          }
        }}
        onCancel={() => {
          setEventPendingDelete(null);
          setShowDeleteConfirm(false);
        }}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      
      {/* Series Delete Confirmation Modal */}
      <ConfirmModal 
        isOpen={showSeriesDeleteConfirm}
        onConfirm={handleConfirmDeleteSeries}
        onCancel={() => {
          setSeriesPendingDelete({ seriesId: null, events: [] });
          setShowSeriesDeleteConfirm(false);
        }}
        title="Delete Entire Series"
        message={`Are you sure you want to delete ${seriesPendingDelete.events.length} time registration${seriesPendingDelete.events.length !== 1 ? 's' : ''} from this series? This action cannot be undone.`}
        confirmText={`Delete ${seriesPendingDelete.events.length} Registration${seriesPendingDelete.events.length !== 1 ? 's' : ''}`}
        confirmButtonClass="bg-orange-600 hover:bg-orange-700"
      />
      
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
      />
      
      {/* Series Modal */}
      <SeriesModal
        isOpen={showSeriesModal}
        onClose={() => {
          setShowSeriesModal(false);
          setSeriesTemplateEvent(null);
        }}
        onCreateSeries={handleBatchCreateSeries}
        templateEvent={seriesTemplateEvent}
        projects={projects}
        canUseBD={canUseBD}
      />

      {/* Admin Panel (roles) */}
      <AdminPanel
        isOpen={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
      />

      {/* Custom Date Range Modal */}
      <CustomDateRangeModal
        isOpen={showCustomDateModal}
        onClose={handleCustomDateRangeClose}
        onApply={handleCustomDateRangeApply}
        currentRange={customDateRange}
        isPeopleContext={customRangeContext === 'people'} // NEW
      />
    </div>
  );
}

App.propTypes = {
  localizer: PropTypes.instanceOf(DateLocalizer),
};

export default App;